import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeIn } from '../../utils/animations';
import { products } from '../../constants/products';


const ProductDetail = () => {
  const { productId } = useParams();
  const [selectedSize, setSelectedSize] = useState('');
  const product = products.find(p => p.id === productId);

  if (!product) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-white">
        <p>Product not found</p>
      </div>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="animate"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Product Image */}
        <motion.div 
          variants={fadeIn}
          className="aspect-square bg-[#1A1A1A] rounded-lg overflow-hidden border border-[#805AD5]/20"
        >
          <img 
            src={product.image} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Product Info */}
        <motion.div 
          variants={fadeIn}
          className="flex flex-col"
        >
          <h1 className="text-3xl font-['Orbitron'] text-white">{product.name}</h1>
          <p className="mt-2 text-gray-400">{product.category}</p>
          
          <div className="mt-4">
            <span className="text-2xl font-bold text-white">${product.price}</span>
            <span className="ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#805AD5]/20 text-[#805AD5]">
              {product.status}
            </span>
          </div>

          <p className="mt-6 text-gray-300">{product.description}</p>

          {/* Size Selector */}
          <div className="mt-8">
            <h3 className="text-sm font-['Orbitron'] text-white">Size</h3>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {['S', 'M', 'L', 'XL'].map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-md transition-all
                    ${selectedSize === size 
                      ? 'bg-[#805AD5] text-white' 
                      : 'bg-[#1A1A1A] text-white border border-[#805AD5]/20 hover:border-[#805AD5]/40'}
                  `}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Add to Cart Button */}
          <button
            className={`
              mt-8 w-full px-6 py-3 rounded-md font-['Orbitron'] flex items-center justify-center transition-all
              ${selectedSize 
                ? 'bg-gradient-to-r from-[#805AD5] to-[#FF4D00] text-white hover:opacity-90' 
                : 'bg-[#1A1A1A] text-gray-400 cursor-not-allowed'}
            `}
            disabled={!selectedSize}
          >
            <ShoppingBag className="mr-2 h-5 w-5" />
            Add to Cart
          </button>

          {/* Features */}
          <div className="mt-8 p-6 bg-[#1A1A1A] rounded-lg border border-[#805AD5]/20">
            <h3 className="text-sm font-['Orbitron'] text-white mb-4">Features</h3>
            <ul className="space-y-2 text-gray-300">
              <li>• Premium quality materials</li>
              <li>• Custom embroidered design</li>
              <li>• Made to order</li>
              <li>• Limited edition</li>
            </ul>
          </div>

          {/* Shipping Info */}
          <div className="mt-8 p-6 bg-[#1A1A1A] rounded-lg border border-[#805AD5]/20">
            <p className="text-sm text-gray-300">
              Pre-order now. Estimated shipping in 2-3 weeks.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProductDetail;